import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function LeadCaptureForm({ open, onClose, defaults = {} }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        property_value: defaults.property_value,
        source: defaults.source || "website",
      });
      setDone(true);
      toast.success("We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({ name: "", email: "", phone: "", message: "" });
    setDone(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-none" data-testid="lead-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl">Speak to a conveyancer</DialogTitle>
              <DialogDescription>
                Share your details and we'll get back to you within one business day with a formal quote and next steps.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2">
              <div>
                <Label htmlFor="ln">Full name</Label>
                <Input
                  id="ln"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="lead-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="le">Email</Label>
                  <Input
                    id="le"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="lead-email"
                  />
                </div>
                <div>
                  <Label htmlFor="lp">Phone</Label>
                  <Input
                    id="lp"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="lead-phone"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lm">Anything else? (optional)</Label>
                <Textarea
                  id="lm"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="lead-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="lead-submit"
              >
                {loading ? "Sending…" : "Request a quote →"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-6" data-testid="lead-success">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <h3 className="font-serif text-3xl mt-4">Thank you</h3>
            <p className="mt-2 text-zinc-600 text-sm">A conveyancer will reach out within one business day.</p>
            <Button onClick={handleClose} className="mt-6 rounded-none bg-zinc-900 hover:bg-zinc-800">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
